import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${window.__BACKEND_URL__}/api`;

// Amber "trial ended / payment failed — add payment" bar shown while the company is
// in its grace period (subscription_status 'grace' or 'past_due'). Billing is the
// account owner's and admins' business only — workers never see it. Jun 2026 / Sep 2026.
const GraceBanner = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const canSeeBilling = user?.is_super_user === true || user?.role === 'admin';

  const refresh = useCallback(() => {
    if (!token || !canSeeBilling) return;
    axios
      .get(`${API}/company/billing-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [token, canSeeBilling]);

  useEffect(() => {
    refresh();
    // Re-check when the tab regains focus (e.g. back from Stripe) so the bar clears on its own.
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(refresh, 5 * 60 * 1000);
    return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(timer); };
  }, [refresh]);

  const handleAddPayment = async () => {
    setPayBusy(true);
    try {
      // Stripe subscription checkout — completing it reactivates instantly
      const r = await axios.post(
        `${API}/stripe/reactivate-checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.already_active) {
        // Paid earlier but webhook was missed — backend just self-healed
        window.location.reload();
        return;
      }
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      navigate('/billing');
    } catch {
      navigate('/billing');
    } finally {
      setPayBusy(false);
    }
  };

  if (!canSeeBilling || !status || !['grace', 'past_due'].includes(status.subscription_status)) return null;

  const end = status.grace_period_until ? new Date(status.grace_period_until) : null;
  const daysLeft = end ? Math.max(0, Math.ceil((end - Date.now()) / 86400000)) : null;
  const isOwner = user?.is_super_user === true;
  const pastDue = status.subscription_status === 'past_due';
  const lead = pastDue ? 'Your last payment didn’t go through' : 'Your free trial has ended';
  const action = pastDue ? 'update your payment method' : 'add a payment method';

  return (
    <div
      className="bg-amber-500 text-amber-950 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
      data-testid="grace-banner"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <p className="text-sm font-medium" data-testid="grace-banner-text">
          {lead}
          {end ? ` — ${action} by ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ` — ${action}`}
          {daysLeft != null ? ` (${daysLeft} day${daysLeft === 1 ? '' : 's'} left)` : ''} to keep your team's access.
        </p>
      </div>
      {isOwner ? (
        <button
          onClick={handleAddPayment}
          disabled={payBusy}
          className="shrink-0 inline-flex items-center gap-1.5 bg-amber-950 text-amber-100 hover:bg-amber-900 disabled:opacity-60 text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
          data-testid="grace-banner-pay-btn"
        >
          <CreditCard className="w-4 h-4" /> {payBusy ? 'Opening checkout…' : pastDue ? 'Update payment' : 'Add payment'}
        </button>
      ) : (
        <span className="shrink-0 text-xs font-semibold" data-testid="grace-banner-owner-hint">
          Ask your account owner to update billing.
        </span>
      )}
    </div>
  );
};

export default GraceBanner;
